import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { DataTable, ListPageLayout, type DataTableColumn } from '@/shared/components/entities'
import { ContextIcon } from '@/shared/components/ui'
import { OverflowMenu } from '@/shared/components/ui/OverflowMenu'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { usePermissions } from '@/modules/units/usePermissions'
import {
  getDocumentDownloadUrl,
  hydrateDocumentFiscalSummaries,
  listDocumentBatches,
  type DocumentBatchWithDocuments,
  type StoredDocument,
} from '@/modules/documents'
import {
  getStoredDocumentType,
  hasBuiltInFiscalLayout,
  isLayoutCompatibleWithDocument,
  listFileLayouts,
  type FileLayoutWithVersion,
  type FiscalDocumentType,
} from '@/modules/file-reader'
import { listAccountingClients } from '@/modules/accounting/clients/accountingClientsService'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import { getAccountingDocumentMovement, hasCurrentAccountingFiscalSummary, taxIdsRepresentSameCompany, type AccountingDocumentMovement } from '@/modules/accounting/accountingDocumentMovement'
import {
  AccountingDocumentDetailsModal,
  IncomingBookkeepingModal,
  OutgoingBookkeepingModal,
  listAccountingDocumentBookkeepingCfops,
  listAccountingDocumentTaxSummary,
  type AccountingDocumentTaxSummaryRow,
} from '@/modules/accounting/bookkeeping'

type WorkflowStatus = 'to_book' | 'to_configure' | 'booked'
type StatusFilter = 'all' | WorkflowStatus | 'summary'
type MovementFilter = 'all' | AccountingDocumentMovement
type TaxSummaryCode = 'IPI' | 'ICMS' | 'PIS' | 'COFINS' | 'CBS' | 'IBS' | 'IS'

interface GroupedTaxSummary {
  cfop: string | null
  situationCode: string | null
  rate: number | null
  totalAmount: number
  baseAmount: number | null
  taxAmount: number | null
}

interface PendingCfopSummary {
  cfop: string
  totalAmount: number
}

interface DocumentRow {
  id: string
  document: StoredDocument
  batch: DocumentBatchWithDocuments
  type: FiscalDocumentType
  workflowStatus: WorkflowStatus
  classificationReady: boolean
}

interface FiscalDocumentSummary {
  summary_version: number
  document_number: string | null
  operation_type: string | null
  issued_at: string | null
  supplier_name: string | null
  issuer_name: string | null
  issuer_tax_id: string | null
  recipient_name: string | null
  recipient_tax_id: string | null
  total_amount: number | null
  item_count: number
  cfops: Array<{ code: string; count: number; total_amount?: number | null }>
}

const TAX_SUMMARY_CODES: TaxSummaryCode[] = ['IPI', 'ICMS', 'PIS', 'COFINS', 'CBS', 'IBS', 'IS']

const typeLabels: Record<FiscalDocumentType, string> = {
  nfe: 'NF-e',
  cte: 'CT-e',
  nfse_nacional: 'NFS-e',
  sped_fiscal: 'SPED Fiscal',
  sped_contributions: 'SPED Contribuições',
  xml: 'XML',
  text: 'Texto estruturado',
  other: 'Outro',
}

const statusClasses: Record<WorkflowStatus, string> = {
  to_book: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  to_configure: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  booked: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
}

const addNullable = (current: number | null, value: number | null) => value === null
  ? current
  : (current || 0) + value

function withFiscalSummary(
  batches: DocumentBatchWithDocuments[],
  documentId: string,
  summary: Record<string, unknown>,
) {
  return batches.map((batch) => ({
    ...batch,
    documents: (batch.documents || []).map((document) => document.id === documentId ? {
      ...document,
      metadata: { ...document.metadata, fiscal_summary: summary },
    } : document),
  }))
}

export function AccountingClientDocumentsPage() {
  const { partyId } = useParams<{ partyId: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t, locale } = useTranslation()
  const { currentUnit, userRole } = useUnit()
  const permissions = usePermissions(userRole)
  const [client, setClient] = useState<AccountingClient | null>(null)
  const [batches, setBatches] = useState<DocumentBatchWithDocuments[]>([])
  const [layouts, setLayouts] = useState<FileLayoutWithVersion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [outgoingBookkeepingRow, setOutgoingBookkeepingRow] = useState<DocumentRow | null>(null)
  const [incomingBookkeepingRow, setIncomingBookkeepingRow] = useState<DocumentRow | null>(null)
  const [detailsRow, setDetailsRow] = useState<DocumentRow | null>(null)
  const [bookkeepingCfops, setBookkeepingCfops] = useState<Record<string, Array<{ code: string; count: number }>>>({})
  const [selectedSummaryTax, setSelectedSummaryTax] = useState<TaxSummaryCode>('ICMS')
  const [taxSummaryRows, setTaxSummaryRows] = useState<AccountingDocumentTaxSummaryRow[]>([])
  const [taxSummaryLoading, setTaxSummaryLoading] = useState(false)
  const [taxSummaryError, setTaxSummaryError] = useState<string | null>(null)
  const [taxSummaryRevision, setTaxSummaryRevision] = useState(0)
  const loadSequence = useRef(0)
  const requestedMovement = searchParams.get('movement')
  const movementFilter: MovementFilter = requestedMovement === 'incoming' || requestedMovement === 'outgoing'
    ? requestedMovement
    : 'all'

  const load = useCallback(async () => {
    if (!currentUnit?.id || !partyId) return
    const sequence = ++loadSequence.current
    setLoading(true)
    setError(null)
    try {
      const [clients, savedLayouts] = await Promise.all([
        listAccountingClients(currentUnit.id),
        listFileLayouts(currentUnit.id),
      ])
      const selectedClient = clients.find((item) => item.party_id === partyId)
      if (!selectedClient) throw new Error('accounting_client_not_found')
      setClient(selectedClient)
      setLayouts(savedLayouts)
      const [loadedBatches, loadedBookkeepingCfops] = await Promise.all([
        listDocumentBatches(selectedClient.client_unit_id),
        listAccountingDocumentBookkeepingCfops(selectedClient.client_unit_id),
      ])
      if (loadSequence.current !== sequence) return
      setBatches(loadedBatches)
      setBookkeepingCfops(Object.fromEntries(loadedBookkeepingCfops.map((entry) => [entry.document_id, entry.cfops])))
      void hydrateDocumentFiscalSummaries(loadedBatches, (documentId, summary) => {
        if (loadSequence.current === sequence) {
          setBatches((current) => withFiscalSummary(current, documentId, summary))
        }
      }).then((hydratedBatches) => {
        if (loadSequence.current === sequence) setBatches(hydratedBatches)
      }).catch((hydrateError) => {
        console.warn('[AccountingClientDocumentsPage] Background summary hydration failed:', hydrateError)
      })
    } catch (loadError) {
      console.error('[AccountingClientDocumentsPage] Error loading client documents:', loadError)
      setError(t('accounting.clientDetails.errors.load'))
    } finally {
      if (loadSequence.current === sequence) setLoading(false)
    }
  }, [currentUnit?.id, partyId, t])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!client?.client_unit_id) {
      setTaxSummaryRows([])
      return
    }
    let cancelled = false
    setTaxSummaryLoading(true)
    setTaxSummaryError(null)
    listAccountingDocumentTaxSummary(client.client_unit_id, selectedSummaryTax)
      .then((summaryRows) => { if (!cancelled) setTaxSummaryRows(summaryRows) })
      .catch((summaryError) => {
        console.error('[AccountingClientDocumentsPage] Tax summary error:', summaryError)
        if (!cancelled) {
          setTaxSummaryRows([])
          setTaxSummaryError(t('accounting.clientDetails.fiscalSummary.loadError'))
        }
      })
      .finally(() => { if (!cancelled) setTaxSummaryLoading(false) })
    return () => { cancelled = true }
  }, [client?.client_unit_id, selectedSummaryTax, taxSummaryRevision, t])

  const rows = useMemo<DocumentRow[]>(() => batches.flatMap((batch) => (batch.documents || []).map((document) => {
    const isBooked = ['processed', 'purged'].includes(document.status)
    const type = getStoredDocumentType(document)
    const hasLayout = hasBuiltInFiscalLayout(type)
      || layouts.some((layout) => isLayoutCompatibleWithDocument(layout, document))
    return {
      id: document.id,
      document,
      batch,
      type,
      workflowStatus: isBooked ? 'booked' : hasLayout ? 'to_book' : 'to_configure',
      classificationReady: hasCurrentAccountingFiscalSummary(document),
    }
  })), [batches, layouts])

  const statusCounts = useMemo(() => ({
    all: rows.filter((row) => movementFilter === 'all' || (row.classificationReady && getAccountingDocumentMovement(row.document, client?.cnpj) === movementFilter)).length,
    to_book: rows.filter((row) => row.workflowStatus === 'to_book' && (movementFilter === 'all' || (row.classificationReady && getAccountingDocumentMovement(row.document, client?.cnpj) === movementFilter))).length,
    to_configure: rows.filter((row) => row.workflowStatus === 'to_configure' && (movementFilter === 'all' || (row.classificationReady && getAccountingDocumentMovement(row.document, client?.cnpj) === movementFilter))).length,
    booked: rows.filter((row) => row.workflowStatus === 'booked' && (movementFilter === 'all' || (row.classificationReady && getAccountingDocumentMovement(row.document, client?.cnpj) === movementFilter))).length,
  }), [client?.cnpj, movementFilter, rows])

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase(locale)
    return rows.filter((row) => {
      if (statusFilter !== 'all' && statusFilter !== 'summary' && row.workflowStatus !== statusFilter) return false
      if (movementFilter !== 'all' && (!row.classificationReady || getAccountingDocumentMovement(row.document, client?.cnpj) !== movementFilter)) return false
      if (!query) return true
      const summary = row.document.metadata?.fiscal_summary as Partial<FiscalDocumentSummary> | undefined
      const cfops = Array.isArray(summary?.cfops) ? summary.cfops.map((cfop) => cfop.code).join(' ') : ''
      const bookedCfops = (bookkeepingCfops[row.document.id] || []).map((cfop) => cfop.code).join(' ')
      return [row.document.original_name, row.batch.original_name, typeLabels[row.type], summary?.issuer_name, summary?.recipient_name, summary?.supplier_name, cfops, bookedCfops]
        .some((value) => value?.toLocaleLowerCase(locale).includes(query))
    })
  }, [bookkeepingCfops, client?.cnpj, locale, movementFilter, rows, searchQuery, statusFilter])

  const counts = useMemo(() => ({
    documents: rows.length,
    duplicates: batches.reduce((total, batch) => total + batch.duplicate_count, 0),
    rejected: batches.reduce((total, batch) => total + batch.rejected_count, 0),
  }), [batches, rows.length])

  const formatDate = (value: string) => new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
  const formatDocumentDate = (value: string | null | undefined) => {
    if (!value) return '—'
    const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value)
    return Number.isNaN(parsed.getTime()) ? value : new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(parsed)
  }
  const formatMoney = (value: number | null | undefined) => typeof value === 'number'
    ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(value)
    : '—'
  const formatRate = (value: number | null | undefined) => typeof value === 'number'
    ? `${new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value)}%`
    : '—'
  const summaryFor = (row: DocumentRow): FiscalDocumentSummary => {
    if (!row.classificationReady) return {
      summary_version: 0,
      document_number: null,
      operation_type: null,
      issued_at: null,
      supplier_name: null,
      issuer_name: null,
      issuer_tax_id: null,
      recipient_name: null,
      recipient_tax_id: null,
      total_amount: null,
      item_count: 0,
      cfops: [],
    }
    const summary = row.document.metadata?.fiscal_summary as Partial<FiscalDocumentSummary> | undefined
    return {
      summary_version: typeof summary?.summary_version === 'number' ? summary.summary_version : 1,
      document_number: typeof summary?.document_number === 'string' ? summary.document_number : null,
      operation_type: typeof summary?.operation_type === 'string' ? summary.operation_type : null,
      issued_at: typeof summary?.issued_at === 'string' ? summary.issued_at : null,
      supplier_name: typeof summary?.supplier_name === 'string' ? summary.supplier_name : null,
      issuer_name: typeof summary?.issuer_name === 'string' ? summary.issuer_name : null,
      issuer_tax_id: typeof summary?.issuer_tax_id === 'string' ? summary.issuer_tax_id : null,
      recipient_name: typeof summary?.recipient_name === 'string' ? summary.recipient_name : null,
      recipient_tax_id: typeof summary?.recipient_tax_id === 'string' ? summary.recipient_tax_id : null,
      total_amount: typeof summary?.total_amount === 'number' ? summary.total_amount : null,
      item_count: typeof summary?.item_count === 'number' ? summary.item_count : 0,
      cfops: Array.isArray(summary?.cfops) ? summary.cfops
        .filter((cfop) => cfop && typeof cfop.code === 'string')
        .map((cfop) => ({
          code: cfop.code,
          count: typeof cfop.count === 'number' ? cfop.count : 0,
          total_amount: typeof cfop.total_amount === 'number' ? cfop.total_amount : null,
        })) : [],
    }
  }
  const formatCfops = (summary: FiscalDocumentSummary) => summary.cfops.length > 0
    ? summary.cfops.map((cfop) => `${cfop.code} (${cfop.count})`).join(', ')
    : '—'
  const formatCfopEntries = (entries: Array<{ code: string; count: number }>) => entries.length > 0
    ? entries.map((cfop) => `${cfop.code} (${cfop.count})`).join(', ')
    : '—'
  const bookkeepingCfopsFor = (row: DocumentRow) => formatCfopEntries(bookkeepingCfops[row.document.id] || [])
  const cfopDisplay = (row: DocumentRow) => {
    const documentCfops = formatCfops(summaryFor(row))
    const isBooked = row.workflowStatus === 'booked'
    const primary = isBooked ? bookkeepingCfopsFor(row) : documentCfops
    return <div className="min-w-0">
      <p className={`truncate font-semibold ${isBooked ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-white'}`} title={primary}>{primary}</p>
      {isBooked && <p className="truncate text-xs text-slate-400 line-through dark:text-slate-500" title={`${t('accounting.documentDetails.bookkeeping.documentCfops')}: ${documentCfops}`}>{documentCfops}</p>}
    </div>
  }
  const groupedTaxSummary = useMemo<GroupedTaxSummary[]>(() => {
    const visibleIds = new Set(filteredRows.map((row) => row.document.id))
    const groups = new Map<string, GroupedTaxSummary>()
    taxSummaryRows.forEach((row) => {
      if (!visibleIds.has(row.document_id)) return
      const key = `${row.cfop || ''}|${row.situation_code || ''}|${row.rate ?? ''}`
      const current = groups.get(key) || {
        cfop: row.cfop,
        situationCode: row.situation_code,
        rate: row.rate,
        totalAmount: 0,
        baseAmount: null,
        taxAmount: null,
      }
      current.totalAmount += row.total_amount
      current.baseAmount = addNullable(current.baseAmount, row.base_amount)
      current.taxAmount = addNullable(current.taxAmount, row.tax_amount)
      groups.set(key, current)
    })
    return Array.from(groups.values()).sort((left, right) => (
      String(left.cfop || '').localeCompare(String(right.cfop || ''))
      || String(left.situationCode || '').localeCompare(String(right.situationCode || ''))
      || (left.rate || 0) - (right.rate || 0)
    ))
  }, [filteredRows, taxSummaryRows])
  const pendingCfopSummary = useMemo<PendingCfopSummary[]>(() => {
    const groups = new Map<string, number>()
    filteredRows
      .filter((row) => row.type === 'nfe' && row.workflowStatus !== 'booked')
      .forEach((row) => {
        const summary = summaryFor(row)
        const cfopsWithTotals = summary.cfops.filter((entry) => typeof entry.total_amount === 'number')
        if (cfopsWithTotals.length === summary.cfops.length && cfopsWithTotals.length > 0) {
          cfopsWithTotals.forEach((entry) => groups.set(entry.code, (groups.get(entry.code) || 0) + (entry.total_amount || 0)))
          return
        }
        const key = summary.cfops.map((entry) => entry.code).sort().join(' / ') || '—'
        groups.set(key, (groups.get(key) || 0) + (summary.total_amount || 0))
      })
    return Array.from(groups, ([cfop, totalAmount]) => ({ cfop, totalAmount }))
      .sort((left, right) => left.cfop.localeCompare(right.cfop))
  }, [filteredRows])
  const counterpartyFor = (row: DocumentRow) => {
    const summary = summaryFor(row)
    const isOwnIssue = taxIdsRepresentSameCompany(client?.cnpj, summary.issuer_tax_id)
    return {
      isOwnIssue,
      label: t(`accounting.clientDetails.counterparty.${isOwnIssue ? 'client' : 'supplier'}`),
      name: isOwnIssue
        ? summary.recipient_name || '—'
        : summary.issuer_name || summary.supplier_name || '—',
    }
  }
  const movementFor = (row: DocumentRow) => {
    if (!row.classificationReady) return {
      isOwnIssue: false,
      label: t('accounting.clientDetails.movement.pending'),
    }
    const { isOwnIssue } = counterpartyFor(row)
    return {
      isOwnIssue,
      label: t(`accounting.clientDetails.movement.${getAccountingDocumentMovement(row.document, client?.cnpj)}`),
    }
  }

  const download = async (path: string) => {
    try {
      window.open(await getDocumentDownloadUrl(path), '_blank', 'noopener,noreferrer')
    } catch (downloadError) {
      console.error('[AccountingClientDocumentsPage] Download error:', downloadError)
      setError(t('accounting.clientPortal.errors.download'))
    }
  }

  const openReader = (row: DocumentRow) => navigate(`/accounting/clients/${partyId}/documents/${row.document.id}/reader`)
  const documentActions = (row: DocumentRow) => {
    const direction = client ? getAccountingDocumentMovement(row.document, client.cnpj) : null
    return [
      ...(permissions.canEditEntries
        && client
        && row.workflowStatus !== 'booked'
        && row.document.document_kind === 'xml'
        && Boolean(row.document.storage_path)
        && row.classificationReady
        && direction === 'outgoing'
        ? [{
            label: t('accounting.bookkeeping.outgoing.action'),
            onClick: () => setOutgoingBookkeepingRow(row),
          }]
        : []),
      ...(permissions.canEditEntries
        && client
        && row.workflowStatus !== 'booked'
        && row.document.document_kind === 'xml'
        && Boolean(row.document.storage_path)
        && row.classificationReady
        && row.type === 'nfe'
        && direction === 'incoming'
        ? [{
            label: t('accounting.bookkeeping.incoming.action'),
            onClick: () => setIncomingBookkeepingRow(row),
          }]
        : []),
      ...(row.document.document_kind === 'xml' && row.document.storage_path ? [{
        label: t('accounting.fileReader.open'),
        onClick: () => openReader(row),
      }] : []),
      ...(row.document.storage_path ? [{
        label: t('accounting.clientPortal.history.download'),
        onClick: () => void download(row.document.storage_path!),
      }] : []),
    ]
  }
  const statusBadge = (row: DocumentRow) => (
    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClasses[row.workflowStatus]}`}>
      {row.workflowStatus === 'to_configure' && <span aria-hidden="true">⚠ </span>}
      {t(`accounting.clientDetails.workflow.${row.workflowStatus}`)}
    </span>
  )

  const columns: DataTableColumn<DocumentRow>[] = [
    { key: 'movement', label: t('accounting.clientDetails.columns.movement'), render: (row) => { const movement = movementFor(row); return <div className="max-w-72"><p className="font-semibold text-slate-900 dark:text-white">{movement.label}</p><div className="mt-1 flex flex-wrap gap-1.5"><span className="rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{typeLabels[row.type]}</span>{movement.isOwnIssue && <span className="rounded-lg bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{t('accounting.clientDetails.ownIssue')}</span>}</div><p className="mt-1 truncate text-xs text-slate-500" title={row.batch.original_name || undefined}>{formatDate(row.document.created_at)} · {row.batch.original_name}</p></div> } },
    { key: 'issued_at', label: t('accounting.clientDetails.columns.documentDate'), render: (row) => formatDocumentDate(summaryFor(row).issued_at) },
    { key: 'number', label: t('accounting.clientDetails.columns.number'), render: (row) => summaryFor(row).document_number || '—' },
    { key: 'cfops', label: t('accounting.documentDetails.bookkeeping.bookkeepingCfops'), render: (row) => <div className="max-w-52">{cfopDisplay(row)}</div> },
    { key: 'total', label: t('accounting.clientDetails.columns.total'), align: 'right', render: (row) => formatMoney(summaryFor(row).total_amount) },
    { key: 'counterparty', label: t('accounting.clientDetails.columns.counterparty'), render: (row) => { const counterparty = counterpartyFor(row); return <div className="max-w-56"><p className="text-xs text-slate-500">{counterparty.label}</p><p className="truncate" title={counterparty.name}>{counterparty.name}</p></div> } },
    { key: 'items', label: t('accounting.clientDetails.columns.items'), align: 'center', render: (row) => row.type === 'nfe' ? summaryFor(row).item_count : '—' },
    { key: 'status', label: t('accounting.clientDetails.columns.status'), render: statusBadge },
    { key: 'actions', label: t('accounting.clientDetails.columns.actions'), align: 'right', render: (row) => documentActions(row).length > 0 ? <div className="flex justify-end" onClick={(event) => event.stopPropagation()}><OverflowMenu items={documentActions(row)} /></div> : null },
  ]

  const chips = (['all', 'to_book', 'to_configure', 'booked', 'summary'] as const).map((status) => (
    <button key={status} type="button" onClick={() => setStatusFilter(status)} className={`shrink-0 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${statusFilter === status ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
      {t(`accounting.clientDetails.filters.${status}`)}{status !== 'summary' ? ` (${statusCounts[status]})` : ''}
    </button>
  ))
  return (
    <ListPageLayout
      title={client?.name || t('accounting.clientDetails.title')}
      description={`${client?.cnpj || t('accounting.clients.noCnpj')} · ${t('accounting.clientDetails.subtitle')}`}
      icon={<ContextIcon context="accounting" className="h-7 w-7 text-emerald-700 dark:text-emerald-400" />}
      backAction={{ label: t('accounting.clientDetails.back'), onClick: () => navigate('/accounting') }}
      searchQuery={searchQuery}
      onSearchChange={setSearchQuery}
      searchPlaceholder={t('accounting.clientDetails.search')}
      clearSearchLabel={t('actions.filter')}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      onMobileViewOverride={setViewMode}
      showViewToggle={statusFilter !== 'summary'}
      viewCardLabel={t('accounting.clients.views.cards')}
      viewTableLabel={t('accounting.clients.views.table')}
      loading={loading}
      error={error}
      titleRight={<span className="hidden rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:inline">{counts.documents} · {counts.duplicates} dup. · {counts.rejected} rej.</span>}
      stickyChips={chips}
      stickyToolbar={!loading && statusFilter !== 'summary' ? <p className="px-1 text-sm text-slate-600 dark:text-slate-400">{t('accounting.clientDetails.showing', { shown: String(filteredRows.length), total: String(rows.length) })}</p> : undefined}
    >
      {statusFilter !== 'summary' && (!loading && filteredRows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-12 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900">{rows.length === 0 ? t('accounting.clientDetails.empty') : t('accounting.clientDetails.noResults')}</div>
      ) : viewMode === 'table' ? (
        <DataTable data={filteredRows} columns={columns} keyExtractor={(row) => row.id} onRowClick={setDetailsRow} />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filteredRows.map((row) => {
            const summary = summaryFor(row)
            const counterparty = counterpartyFor(row)
            const movement = movementFor(row)
            return (
            <article key={row.id} role="button" tabIndex={0} onClick={() => setDetailsRow(row)} onKeyDown={(event) => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) setDetailsRow(row) }} className="cursor-pointer overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40 dark:border-slate-700 dark:bg-slate-900">
              <div className="border-b border-slate-100 p-5 dark:border-slate-800"><div className="flex items-start justify-between gap-3"><div className="min-w-0"><h2 className="font-semibold text-slate-900 dark:text-white">{movement.label}</h2><div className="mt-2 flex flex-wrap gap-2"><span className="inline-flex rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{typeLabels[row.type]}</span>{movement.isOwnIssue && <span className="inline-flex rounded-lg bg-violet-100 px-2.5 py-1 text-xs font-bold text-violet-700 dark:bg-violet-900/30 dark:text-violet-300">{t('accounting.clientDetails.ownIssue')}</span>}</div></div><div className="flex shrink-0 items-center gap-1">{statusBadge(row)}{documentActions(row).length > 0 && <OverflowMenu items={documentActions(row)} />}</div></div>{row.workflowStatus === 'to_configure' && <p className="mt-3 text-xs font-medium text-amber-700 dark:text-amber-300">{t('accounting.clientDetails.withoutLayout')}</p>}</div>
              <dl className="grid grid-cols-4 gap-4 p-5 text-sm">
                <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.documentDate')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatDocumentDate(summary.issued_at)}</dd></div>
                <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.number')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{summary.document_number || '—'}</dd></div>
                <div><dt className="text-xs text-slate-500">{t(row.workflowStatus === 'booked' ? 'accounting.documentDetails.bookkeeping.bookkeepingCfops' : 'accounting.documentDetails.bookkeeping.documentCfops')}</dt><dd className="mt-1">{cfopDisplay(row)}</dd></div>
                <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.total')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(summary.total_amount)}</dd></div>
                <div className={`${row.type === 'nfe' ? 'col-span-3' : 'col-span-4'} min-w-0`}><dt className="text-xs text-slate-500">{counterparty.label}</dt><dd className="mt-1 truncate font-semibold text-slate-900 dark:text-white" title={counterparty.name}>{counterparty.name}</dd></div>
                {row.type === 'nfe' && <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.items')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{summary.item_count}</dd></div>}
              </dl>
              <div className="border-t border-slate-100 px-5 py-3 text-xs text-slate-400 dark:border-slate-800">{formatDate(row.document.created_at)} · {row.batch.original_name}</div>
            </article>
            )
          })}
        </div>
      ))}

      {!loading && statusFilter === 'summary' && <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <header className="flex flex-col gap-4 border-b border-slate-200 p-5 sm:flex-row sm:items-end sm:justify-between dark:border-slate-700">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">{t('accounting.clientDetails.fiscalSummary.title')}</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.description')}</p>
          </div>
          <label className="w-full sm:w-48">
            <span className="mb-1 block text-xs font-medium text-slate-500 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.tax')}</span>
            <select value={selectedSummaryTax} onChange={(event) => setSelectedSummaryTax(event.target.value as TaxSummaryCode)} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-800 dark:text-white">
              {TAX_SUMMARY_CODES.map((taxCode) => <option key={taxCode} value={taxCode}>{taxCode}</option>)}
            </select>
          </label>
        </header>

        <div className="space-y-7 p-5">
          <section>
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t('accounting.clientDetails.fiscalSummary.booked')}</h3>
            {taxSummaryLoading ? (
              <p className="mt-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.loading')}</p>
            ) : taxSummaryError ? (
              <p className="mt-3 rounded-xl bg-rose-50 p-4 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{taxSummaryError}</p>
            ) : groupedTaxSummary.length === 0 ? (
              <p className="mt-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.emptyBooked')}</p>
            ) : (
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                {groupedTaxSummary.map((group) => <article key={`${group.cfop}-${group.situationCode}-${group.rate}`} className="rounded-xl border border-slate-200 p-4 dark:border-slate-700">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-slate-900 dark:text-white">{t('accounting.clientDetails.fiscalSummary.cfop')} {group.cfop || '—'}</p>
                    <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300">{t('accounting.clientDetails.fiscalSummary.cst')} {group.situationCode || '—'}</span>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.fiscalSummary.total')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(group.totalAmount)}</dd></div>
                    <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.fiscalSummary.base')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatMoney(group.baseAmount)}</dd></div>
                    <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.fiscalSummary.rate')}</dt><dd className="mt-1 font-semibold text-slate-900 dark:text-white">{formatRate(group.rate)}</dd></div>
                    <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.fiscalSummary.taxAmount', { tax: selectedSummaryTax })}</dt><dd className="mt-1 font-semibold text-emerald-700 dark:text-emerald-300">{formatMoney(group.taxAmount)}</dd></div>
                  </dl>
                </article>)}
              </div>
            )}
          </section>

          <section className="border-t border-slate-200 pt-6 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">{t('accounting.clientDetails.fiscalSummary.pending')}</h3>
            {pendingCfopSummary.length === 0 ? (
              <p className="mt-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-800/60 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.emptyPending')}</p>
            ) : (
              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {pendingCfopSummary.map((group) => <article key={group.cfop} className="flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-900/60 dark:bg-amber-950/20">
                  <div><p className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.originalCfop')}</p><p className="mt-1 font-semibold text-slate-900 dark:text-white">{group.cfop}</p></div>
                  <div className="text-right"><p className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.clientDetails.fiscalSummary.total')}</p><p className="mt-1 whitespace-nowrap font-semibold text-slate-900 dark:text-white">{formatMoney(group.totalAmount)}</p></div>
                </article>)}
              </div>
            )}
          </section>
        </div>
      </section>}

      {currentUnit?.id && client && <OutgoingBookkeepingModal officeUnitId={currentUnit.id} client={client} document={outgoingBookkeepingRow?.document || null} isOpen={Boolean(outgoingBookkeepingRow)} onClose={() => setOutgoingBookkeepingRow(null)} onSaved={() => { setTaxSummaryRevision((revision) => revision + 1); void load() }} />}
      {currentUnit?.id && client && <IncomingBookkeepingModal officeUnitId={currentUnit.id} client={client} document={incomingBookkeepingRow?.document || null} isOpen={Boolean(incomingBookkeepingRow)} onClose={() => setIncomingBookkeepingRow(null)} onSaved={() => { setTaxSummaryRevision((revision) => revision + 1); void load() }} />}
      {client && currentUnit?.id && <AccountingDocumentDetailsModal officeUnitId={currentUnit.id} client={client} document={detailsRow?.document || null} documentTypeLabel={detailsRow ? typeLabels[detailsRow.type] : ''} movementLabel={detailsRow ? movementFor(detailsRow).label : ''} workflowLabel={detailsRow ? t(`accounting.clientDetails.workflow.${detailsRow.workflowStatus}`) : ''} counterpartyLabel={detailsRow ? counterpartyFor(detailsRow).label : ''} counterpartyName={detailsRow ? counterpartyFor(detailsRow).name : ''} showItemCount={detailsRow?.type === 'nfe'} isBooked={detailsRow?.workflowStatus === 'booked'} isOpen={Boolean(detailsRow)} onClose={() => setDetailsRow(null)} />}
    </ListPageLayout>
  )
}
